// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The artefacts.
 *
 * They come out as ONE zip from ONE click, not four downloads. Safari silently
 * suppresses every programmatic download after the first in a single gesture,
 * and Chrome's limiter flips the second one to a permission prompt — so a
 * four-file flow hands a phone user one file and no error.
 *
 * The splitter config is the thing most people came for, so it is written to be
 * pasted rather than read: real numbers filled in, the tokenizer named, and the
 * mismatches that will silently change those numbers called out by name.
 */

import { zipSync, strToU8 } from 'fflate';
import type { RunScore } from './score.ts';
import type { ModelEntry } from './registry.ts';
import type { StrategyId } from './split.ts';

export interface ChunkRow {
  id: number;
  source_file: string;
  page: number;
  char_start: number;
  char_end: number;
  token_count: number;
  heading_prefix: string;
  text: string;
}

export const STRATEGY_LABEL: Record<StrategyId, string> = {
  fixed: 'Fixed-size token windows',
  recursive: 'Recursive separator (LangChain default)',
  heading: 'Heading-aware',
};

export function chunksJsonl(rows: readonly ChunkRow[]): string {
  // No rows means an EMPTY file, not a file containing one blank line. `for line
  // in open(f): json.loads(line)` — the way almost everyone reads JSONL — raises
  // on that blank line, so the degenerate export would look like a corrupt one.
  if (rows.length === 0) return '';
  return rows.map((r) => JSON.stringify(r)).join('\n') + '\n';
}

/* ── CSV ──────────────────────────────────────────────────────────────────── */

/* Both patterns are built from source strings rather than written as regex
 * literals. A literal control character inside a regex is invisible in review,
 * and the suite's source-hygiene gate refuses any file in `src/` containing one
 * — it caught two real ones during this build. */
/* U+0009 TAB is in this class deliberately. OWASP lists it alongside `=+-@`
 * as a formula trigger, because importers strip leading whitespace before
 * deciding whether a cell is a formula — so a cell of TAB + "=SUM(A1)" executes
 * even though its first character is not one of the four guarded ones. Escaping
 * it to a visible \x09 removes the vector outright and makes the promise two
 * lines below ("control characters become visible escapes") true of every
 * control character rather than most of them. */
const CONTROL_CHARS = new RegExp('[\\u0000-\\u0009\\u000b\\u000c\\u000e-\\u001f\\u007f]', 'g');
const NEWLINES = new RegExp('\\r\\n|\\r|\\n', 'g');
const NEEDS_QUOTING = new RegExp('["|,;\\t]');

/**
 * RFC 4180, with the three things a spreadsheet needs and a naive writer omits:
 * a leading `=+-@` is quoted AND apostrophe-guarded so a cell cannot execute as
 * a formula, control characters become visible escapes so one record is always
 * one physical line, and `;` forces quoting for German, French and Spanish Excel.
 */
export function csvCell(value: string | number): string {
  let s = String(value ?? '');
  s = s.replace(CONTROL_CHARS, (c) => '\\x' + c.charCodeAt(0).toString(16).padStart(2, '0'));
  s = s.replace(NEWLINES, '\\n');
  const risky = /^[=+\-@]/.test(s);
  if (risky) s = "'" + s;
  if (risky || NEEDS_QUOTING.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function toCsv(headers: readonly string[], rows: readonly (string | number)[][]): string {
  const lines = [headers.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))];
  // A UTF-8 BOM and CRLF, because Excel needs both to open the file correctly.
  return '﻿' + lines.join('\r\n') + '\r\n';
}

export function comparisonCsv(scores: readonly RunScore[]): string {
  // No column here is called recall, precision or IoU. Those names belong to the
  // published chunking-evaluation metric, which is computed over pre-annotated
  // relevant excerpts that chunkforge deliberately does not ask for; borrowing
  // the name would invite a comparison the numbers cannot support.
  const headers = [
    'strategy',
    'chunk_size',
    'chunk_overlap',
    'results_retrieved_k',
    'chunks',
    'median_tokens',
    'oversized_chunks',
    'marked_queries',
    'answers_retrieved',
    'mean_retrieved_tokens',
    'chance_level',
    'top_k_covers_half_the_corpus',
  ];
  const rows = scores.map((s) => [
    s.strategy,
    s.size,
    s.overlap,
    s.k,
    s.chunkCount,
    s.medianTokens,
    s.oversized,
    s.markedCount,
    s.retrievedCount,
    Math.round(s.meanRetrievedTokens),
    s.chanceLevel.toFixed(4),
    s.degenerate ? 'yes' : 'no',
  ]);
  return toCsv(headers, rows);
}

/* ── splitter config ──────────────────────────────────────────────────────── */

export interface ConfigContext {
  strategy: StrategyId;
  size: number;
  overlap: number;
  model: ModelEntry;
  chunkCount: number;
  oversized: number;
  medianTokens: number;
  /** Tokens the user is willing to paste into their model; k is derived from it. */
  budget: number;
  /** Results retrieved at this chunk size — floor(budget / size). */
  topK: number;
}

/**
 * The copy-paste line, and the warnings that stop it being wrong the moment it
 * is pasted.
 *
 * Two of those warnings are the whole reason this is not a one-liner. LangChain
 * measures `chunk_size` with whatever `length_function` you give it and DEFAULTS
 * TO CHARACTERS, so pasting a token number into a default splitter silently
 * chunks four to five times smaller than intended. LlamaIndex measures in
 * tokens, but its default tokenizer is tiktoken's cl100k regardless of which
 * embedding model you then call — read out of llama-index-core 0.14.16, whose
 * `get_tokenizer()` calls `tiktoken.encoding_for_model("gpt-3.5-turbo")`.
 */
export function splitterConfig(ctx: ConfigContext): string {
  const { size, overlap, model, strategy } = ctx;
  const hf = model.kind === 'wordpiece';
  const lines: string[] = [];

  lines.push('# chunkforge — splitter configuration');
  lines.push('');
  lines.push(`Strategy: **${STRATEGY_LABEL[strategy]}**`);
  lines.push(`Chunk size: **${size} tokens**  ·  Overlap: **${overlap} tokens**`);
  lines.push(`Counted with: **${model.label}** (\`${model.repo}\`)`);
  lines.push(
    `Produced ${ctx.chunkCount} chunks, median ${ctx.medianTokens} tokens${ctx.oversized > 0 ? `, ${ctx.oversized} over the limit` : ''}.`,
  );
  lines.push('');

  if (size > model.maxTokens) {
    lines.push(`> **This chunk size exceeds ${model.label}'s documented ${model.maxTokens}-token input limit.**`);
    lines.push('> Embedding APIs do not error on this — they truncate, so the tail of every');
    lines.push('> oversized chunk is simply not in the vector.');
    lines.push('');
  }

  if (strategy === 'recursive') {
    lines.push('## LangChain (Python)');
    lines.push('');
    lines.push('```python');
    lines.push('from langchain_text_splitters import RecursiveCharacterTextSplitter');
    if (hf) {
      lines.push('from transformers import AutoTokenizer');
      lines.push('');
      lines.push(`tokenizer = AutoTokenizer.from_pretrained("${model.repo}")`);
      lines.push('');
      lines.push('splitter = RecursiveCharacterTextSplitter.from_huggingface_tokenizer(');
      lines.push('    tokenizer,');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push('    separators=["\\n\\n", "\\n", " ", ""],');
      lines.push(')');
    } else {
      lines.push('');
      lines.push('splitter = RecursiveCharacterTextSplitter.from_tiktoken_encoder(');
      lines.push('    encoding_name="cl100k_base",');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push('    separators=["\\n\\n", "\\n", " ", ""],');
      lines.push(')');
    }
    lines.push('```');
    lines.push('');
    lines.push('These are the exact chunks chunkforge showed you: the recursive splitter here is a');
    lines.push('reimplementation of `langchain_text_splitters` 0.3.11, checked chunk for chunk');
    lines.push('against the library itself across seven size and overlap settings.');
    lines.push('');
    lines.push('## LangChain (JavaScript)');
    lines.push('');
    lines.push('```js');
    lines.push("import { RecursiveCharacterTextSplitter } from '@langchain/textsplitters';");
    lines.push('');
    lines.push('const splitter = new RecursiveCharacterTextSplitter({');
    lines.push(`  chunkSize: ${size},`);
    lines.push(`  chunkOverlap: ${overlap},`);
    lines.push("  separators: ['\\n\\n', '\\n', ' ', ''],");
    lines.push('});');
    lines.push('```');
    lines.push('');
    lines.push('> The JavaScript splitter counts `chunkSize` in CHARACTERS unless you give it a');
    lines.push('> token-based length function. Pasting the number above unchanged produces chunks');
    lines.push('> roughly four times smaller than the ones you measured.');
  } else if (strategy === 'fixed') {
    lines.push('## LangChain (Python)');
    lines.push('');
    lines.push('```python');
    if (hf) {
      lines.push('from langchain_text_splitters import RecursiveCharacterTextSplitter');
      lines.push('from transformers import AutoTokenizer');
      lines.push('');
      lines.push(`tokenizer = AutoTokenizer.from_pretrained("${model.repo}")`);
      lines.push('');
      lines.push('splitter = RecursiveCharacterTextSplitter.from_huggingface_tokenizer(');
      lines.push('    tokenizer,');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push('    separators=[""],  # no structure at all — pure token windows');
      lines.push(')');
    } else {
      lines.push('from langchain_text_splitters import TokenTextSplitter');
      lines.push('');
      lines.push('splitter = TokenTextSplitter(');
      lines.push('    encoding_name="cl100k_base",');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push(')');
    }
    lines.push('```');
  } else {
    lines.push('## Heading-aware chunking');
    lines.push('');
    lines.push('No stock splitter reproduces this one, and chunkforge will not pretend otherwise.');
    lines.push('It cuts at every heading, keeps each section whole when it fits, splits the ones');
    lines.push('that do not with the recursive splitter below, and prepends the heading trail to');
    lines.push('every piece before embedding — so a section titled "Taxis and late-night travel"');
    lines.push('stays findable from a chunk whose body only says "the journey" and "the fare".');
    lines.push('');
    lines.push("The closest stock equivalents are LangChain's `MarkdownHeaderTextSplitter` (Markdown");
    lines.push("only, and it does not prepend the trail) and LlamaIndex's `MarkdownNodeParser`. To");
    lines.push('reproduce what you measured, split on headings yourself, prepend the trail, and use');
    lines.push('this configuration for the sections that are too long:');
    lines.push('');
    lines.push('```python');
    lines.push('from langchain_text_splitters import RecursiveCharacterTextSplitter');
    if (hf) {
      lines.push('from transformers import AutoTokenizer');
      lines.push('');
      lines.push(`tokenizer = AutoTokenizer.from_pretrained("${model.repo}")`);
      lines.push('');
      // The bare constructor measures chunk_size in CHARACTERS. Emitting it with a
      // token number in it would ship, inside the heading branch, precisely the
      // mistake this file warns about three sections further down.
      lines.push('inner = RecursiveCharacterTextSplitter.from_huggingface_tokenizer(');
      lines.push('    tokenizer,');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push('    separators=["\\n\\n", "\\n", " ", ""],');
      lines.push(')');
    } else {
      lines.push('');
      lines.push('inner = RecursiveCharacterTextSplitter.from_tiktoken_encoder(');
      lines.push('    encoding_name="cl100k_base",');
      lines.push(`    chunk_size=${size},`);
      lines.push(`    chunk_overlap=${overlap},`);
      lines.push('    separators=["\\n\\n", "\\n", " ", ""],');
      lines.push(')');
    }
    lines.push('```');
  }

  lines.push('');
  lines.push('## LlamaIndex');
  lines.push('');
  lines.push('```python');
  lines.push('from llama_index.core.node_parser import SentenceSplitter');
  lines.push('');
  lines.push('splitter = SentenceSplitter(');
  lines.push(`    chunk_size=${size},`);
  lines.push(`    chunk_overlap=${overlap},`);
  lines.push(')');
  lines.push('```');
  lines.push('');
  lines.push('> `SentenceSplitter` is sentence-aware and will NOT reproduce these exact chunks — it');
  lines.push('> avoids cutting mid-sentence, which the recursive splitter does not. Treat the');
  lines.push('> numbers as a starting point there rather than as a result.');
  if (hf) {
    lines.push('>');
    lines.push('> It also counts tokens with tiktoken `cl100k_base` by default, whatever embedding');
    lines.push(`> model you go on to call. You measured in **${model.label}** tokens, so set`);
    lines.push("> `Settings.tokenizer` to your model's tokenizer or the size above means something");
    lines.push('> different in your pipeline than it does here.');
  }

  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## What this configuration was chosen against');
  lines.push('');
  lines.push(`Retrieval was scored by retrieving k=${ctx.topK} chunks — floor(${ctx.budget} / ${size}), derived from a`);
lines.push('context budget rather than fixed, so every chunk size was compared at the same price — with a');
lines.push('static embedding model');
  lines.push('(`sentence-transformers/static-retrieval-mrl-en-v1`), a fast stand-in and a weaker');
  lines.push('retriever than the one you will deploy. It answers "was the passage reachable at this');
  lines.push('chunk size", not "what will your embedder rank first".');
  lines.push('');
  lines.push('Generated by chunkforge — https://chunkforge.benrichardson.dev');
  return lines.join('\n') + '\n';
}

/* ── report ───────────────────────────────────────────────────────────────── */

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ESCAPES[c]);
}

export interface ReportContext extends ConfigContext {
  headline: string;
  queries: { text: string; marked: boolean; retrieved: boolean; rank: number | null; chunkCount: number; retrievedTokens: number }[];
  sweep: readonly RunScore[];
  documents: { name: string; tokens: number; pages: number }[];
  generatedAt: string;
}

/**
 * A self-contained report — no script, no link, no src, no network.
 *
 * Every string that reaches it is escaped, because document names and query text
 * are the user's own input and the file gets opened from `file://`, where a
 * script would run with no origin to contain it.
 */
export function reportHtml(ctx: ReportContext): string {
  const rows = ctx.sweep
    .map(
      (s) =>
        `<tr><td>${esc(STRATEGY_LABEL[s.strategy as StrategyId] ?? s.strategy)}</td><td>${s.size}</td><td>${s.overlap}</td><td>${s.k}</td><td>${s.chunkCount}</td><td>${s.medianTokens}</td><td>${s.retrievedCount} of ${s.markedCount}</td><td>${Math.round(s.meanRetrievedTokens)}</td><td>${(s.chanceLevel * 100).toFixed(0)}%${s.degenerate ? ' (degenerate)' : ''}</td></tr>`,
    )
    .join('\n');

  const queryRows = ctx.queries
    .map(
      (q) =>
        `<tr><td>${esc(q.text)}</td><td>${q.marked ? 'marked' : 'not marked'}</td><td>${q.marked ? (q.retrieved ? 'yes' : 'no') : '—'}</td><td>${q.rank ? `${q.rank} of ${q.chunkCount}` : '—'}</td><td>${q.retrievedTokens}</td></tr>`,
    )
    .join('\n');

  const docRows = ctx.documents.map((d) => `<tr><td>${esc(d.name)}</td><td>${d.tokens}</td><td>${d.pages}</td></tr>`).join('\n');

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>chunkforge report</title>
<style>
:root{color-scheme:light dark}
body{font:14px/1.6 -apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;margin:0;padding:2rem 1rem;background:#fff;color:#16171d}
main{max-width:900px;margin:0 auto}
h1{font-size:1.4rem;margin:0 0 .25rem}
h2{font-size:1rem;margin:2rem 0 .5rem;border-bottom:1px solid #e7e8ee;padding-bottom:.35rem}
p.sub{color:#767b8d;margin:0 0 1.5rem}
.headline{font-size:1.15rem;line-height:1.45;border-left:3px solid #6d3fe0;padding:.5rem 0 .5rem 1rem;margin:1.5rem 0}
table{border-collapse:collapse;width:100%;font-size:.82rem}
th,td{text-align:left;padding:.4rem .6rem;border-bottom:1px solid #e7e8ee;white-space:nowrap}
th{color:#767b8d;font-size:.7rem;text-transform:uppercase;letter-spacing:.05em}
td{font-family:ui-monospace,'SF Mono',Consolas,monospace}
td:first-child{font-family:inherit;white-space:normal}
.wrap{overflow-x:auto}
footer{margin-top:2.5rem;color:#767b8d;font-size:.78rem;border-top:1px solid #e7e8ee;padding-top:1rem}
@media (prefers-color-scheme:dark){body{background:#0f1015;color:#e9eaef}th,td,h2,footer{border-color:#22242f}p.sub,th,footer{color:#a3a7b8}.headline{border-left-color:#a78bfa}}
</style></head><body><main>
<h1>chunkforge report</h1>
<p class="sub">Generated ${esc(ctx.generatedAt)} · ${esc(STRATEGY_LABEL[ctx.strategy])} · ${ctx.size} tokens, ${ctx.overlap} overlap · counted with ${esc(ctx.model.label)}</p>
<div class="headline">${esc(ctx.headline)}</div>
<h2>Queries</h2>
<div class="wrap"><table><thead><tr><th>Query</th><th>Ground truth</th><th>Answer retrieved</th><th>First hit</th><th>Retrieved tokens</th></tr></thead><tbody>
${queryRows}
</tbody></table></div>
<h2>Every setting measured</h2>
<div class="wrap"><table><thead><tr><th>Strategy</th><th>Size</th><th>Overlap</th><th>Results (k)</th><th>Chunks</th><th>Median tokens</th><th>Answers retrieved</th><th>Retrieved tokens</th><th>Chance level</th></tr></thead><tbody>
${rows}
</tbody></table></div>
<h2>Documents</h2>
<div class="wrap"><table><thead><tr><th>File</th><th>Tokens</th><th>Pages</th></tr></thead><tbody>
${docRows}
</tbody></table></div>
<footer>
<p><strong>How to read this.</strong> "Answers retrieved" counts how many of the passages you marked came back at all, out of how many you marked — the denominator is always printed beside it. A passage counts as retrieved when one of the returned chunks overlaps it by at least one character. The number of results, k, is DERIVED from your context budget of ${ctx.budget} tokens as floor(budget / chunk size), so every chunk size is compared at the same price rather than letting a bigger chunk retrieve more text for free. "Chance level" is exactly what random selection would score at that setting; a row close to it has measured nothing. Rank is shown beside the number of chunks it was drawn from, because rank alone is not comparable across chunk sizes.</p>
<p>This is not the metric published in Smith and Troynikov's chunking-evaluation report, and it does not use their names. Theirs is computed over pre-annotated relevant excerpts; this is a simpler measurement over passages you marked yourself, and it is a LOWER BOUND — other passages in your documents may answer the query just as well and were never marked.</p>
<p>Scoring used a static embedding model (sentence-transformers/static-retrieval-mrl-en-v1), a fast stand-in that is a weaker retriever than a deployed transformer. It answers whether the passage was reachable at a given chunk size, not how your production embedder will rank it.</p>
<p>chunkforge — https://chunkforge.benrichardson.dev</p>
</footer>
</main></body></html>
`;
}

/* ── packaging ────────────────────────────────────────────────────────────── */

export function buildZip(files: Record<string, string>): Blob {
  const entries: Record<string, Uint8Array> = {};
  for (const [name, text] of Object.entries(files)) entries[name] = strToU8(text);
  const packed = zipSync(entries, { level: 6 });
  return new Blob([packed as unknown as BlobPart], { type: 'application/zip' });
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
